/** Actionable downgrade violations — label + where to fix in the admin UI. */
export interface SettingViolationRoute {
  commands: string[];
  queryParams?: Record<string, string>;
}

export interface SettingViolation {
  code: string;
  label: string;
  fixHint: string;
  where: string;
  route: SettingViolationRoute;
  affectedCount?: number;
}

const SETTINGS_SCREENSHOTS = 'Employee Settings → Screenshots';
const SETTINGS_TRACKING = 'Employee Settings → Tracking Controls';
const SETTINGS_SHIFTS = 'Employee Settings → Shifts';
const SETTINGS_PER_EMPLOYEE = 'Employee Settings → select employee → Screenshots / Tracking';

const ROUTE_SCREENSHOTS: SettingViolationRoute = {
  commands: ['/employee/setting'],
  queryParams: { tab: 'screenshots' },
};
const ROUTE_TRACKING: SettingViolationRoute = {
  commands: ['/employee/setting'],
  queryParams: { tab: 'tracking' },
};
const ROUTE_SHIFTS: SettingViolationRoute = {
  commands: ['/employee/setting'],
  queryParams: { tab: 'shifts' },
};
const ROUTE_BULK_UPDATE: SettingViolationRoute = {
  commands: ['/employee/setting'],
  queryParams: { tab: 'bulk-update' },
};
const ROUTE_EMPLOYEES: SettingViolationRoute = { commands: ['/employees'] };
const ROUTE_WORK_REPLAY: SettingViolationRoute = { commands: ['/work-replay'] };
const ROUTE_PRODUCTIVITY_TRENDS: SettingViolationRoute = { commands: ['/productivity-trends'] };
const ROUTE_REPORTS: SettingViolationRoute = { commands: ['/reports'] };

export const SETTING_VIOLATION_CATALOG: Record<string, Omit<SettingViolation, 'label'> & { label?: string }> = {
  app_switch: {
    code: 'app_switch',
    fixHint: 'Turn off the “App-switch screenshots” toggle.',
    where: SETTINGS_SCREENSHOTS,
    route: ROUTE_SCREENSHOTS,
  },
  webcam: {
    code: 'webcam',
    fixHint: 'Turn off the “Webcam capture” toggle.',
    where: SETTINGS_SCREENSHOTS,
    route: ROUTE_SCREENSHOTS,
  },
  periodic_interval: {
    code: 'periodic_interval',
    fixHint: 'Set “Periodic screenshots” interval to at least 15 minutes.',
    where: SETTINGS_SCREENSHOTS,
    route: ROUTE_SCREENSHOTS,
  },
  strict_tracking: {
    code: 'strict_tracking',
    fixHint: 'Turn off “Strict time tracking”.',
    where: SETTINGS_TRACKING,
    route: ROUTE_TRACKING,
  },
  auto_tracking: {
    code: 'auto_tracking',
    fixHint: 'Turn off “Auto Tracking (Restricted Mode)”.',
    where: SETTINGS_TRACKING,
    route: ROUTE_TRACKING,
  },
  hidden_mode: {
    code: 'hidden_mode',
    fixHint: 'Switch employees from Hidden Mode to Employee Controlled (use the action above).',
    where: 'Employees → Agent Info',
    route: ROUTE_EMPLOYEES,
  },
  custom_shift: {
    code: 'custom_shift',
    fixHint: 'Remove the custom shift or assign the default workspace shift.',
    where: SETTINGS_SHIFTS,
    route: ROUTE_SHIFTS,
  },
  work_replay: {
    code: 'work_replay',
    fixHint: 'Work Replay will be disabled on Basic — export sessions first.',
    where: 'Work Replay (feature access)',
    route: ROUTE_WORK_REPLAY,
  },
  productivity_trends: {
    code: 'productivity_trends',
    fixHint: 'Productivity Trends full access ends on Basic — export reports if needed.',
    where: 'Reports → Productivity Trends',
    route: ROUTE_PRODUCTIVITY_TRENDS,
  },
  advanced_filters: {
    code: 'advanced_filters',
    fixHint: 'Advanced reports will be removed — export first.',
    where: 'Reports',
    route: ROUTE_REPORTS,
  },
  executive_reports: {
    code: 'executive_reports',
    fixHint: 'Executive reports are not on the target plan — export if needed.',
    where: 'Reports → Executive',
    route: ROUTE_PRODUCTIVITY_TRENDS,
  },
  extended_retention: {
    code: 'extended_retention',
    fixHint: 'Longer history stays during grace period — export before cleanup.',
    where: 'Data Management',
    route: { commands: ['/data-management'] },
  },
  unknown: {
    code: 'unknown',
    fixHint: 'Update this setting before downgrading.',
    where: SETTINGS_PER_EMPLOYEE,
    route: ROUTE_BULK_UPDATE,
  },
};

/** Feature-loss / export reminders — informational only, never block downgrade. */
export const INFORMATIONAL_VIOLATION_CODES = new Set<string>([
  'work_replay',
  'productivity_trends',
  'advanced_filters',
  'executive_reports',
  'extended_retention',
]);

export function isInformationalSettingViolation(code: string | undefined): boolean {
  return !!code && INFORMATIONAL_VIOLATION_CODES.has(code);
}

export function splitSettingViolations(violations: SettingViolation[]): {
  blocking: SettingViolation[];
  informational: SettingViolation[];
} {
  const blocking: SettingViolation[] = [];
  const informational: SettingViolation[] = [];
  for (const violation of violations) {
    if (isInformationalSettingViolation(violation.code)) {
      informational.push(violation);
    } else {
      blocking.push(violation);
    }
  }
  return { blocking, informational };
}

export function buildSettingViolation(
  code: keyof typeof SETTING_VIOLATION_CATALOG | string,
  label: string
): SettingViolation {
  const template = SETTING_VIOLATION_CATALOG[code] ?? SETTING_VIOLATION_CATALOG.unknown;
  return {
    code: template.code,
    label,
    fixHint: template.fixHint,
    where: template.where,
    route: template.route,
  };
}

/** Merge API/partial details with catalog routes for navigation. */
export function enrichSettingDetail(
  detail: Partial<SettingViolation> & { label: string }
): SettingViolation {
  const fromCode = detail.code ? SETTING_VIOLATION_CATALOG[detail.code] : undefined;
  const resolved = fromCode
    ? buildSettingViolation(detail.code!, detail.label)
    : resolveViolationFromLabel(detail.label);

  return {
    code: detail.code ?? resolved.code,
    label: detail.label,
    fixHint: detail.fixHint ?? resolved.fixHint,
    where: detail.where ?? resolved.where,
    route: resolved.route,
    affectedCount: detail.affectedCount,
  };
}

export function resolveViolationFromLabel(label: string): SettingViolation {
  const lower = label.toLowerCase();
  if (lower.includes('app-switch') || lower.includes('app switch')) {
    return buildSettingViolation('app_switch', label);
  }
  if (lower.includes('webcam')) {
    return buildSettingViolation('webcam', label);
  }
  if (lower.includes('screenshot interval') || lower.includes('periodic screenshot')) {
    return buildSettingViolation('periodic_interval', label);
  }
  if (lower.includes('strict time')) {
    return buildSettingViolation('strict_tracking', label);
  }
  if (lower.includes('automatic tracking') || lower.includes('auto tracking')) {
    return buildSettingViolation('auto_tracking', label);
  }
  if (lower.includes('hidden')) {
    return buildSettingViolation('hidden_mode', label);
  }
  if (lower.includes('shift')) {
    return buildSettingViolation('custom_shift', label);
  }
  if (lower.includes('work replay')) {
    return buildSettingViolation('work_replay', label);
  }
  if (lower.includes('productivity trends')) {
    return buildSettingViolation('productivity_trends', label);
  }
  if (lower.includes('advanced report') || lower.includes('report filter')) {
    return buildSettingViolation('advanced_filters', label);
  }
  if (lower.includes('executive')) {
    return buildSettingViolation('executive_reports', label);
  }
  if (lower.includes('extended retention') || lower.includes('extended data retention')) {
    return buildSettingViolation('extended_retention', label);
  }
  return buildSettingViolation('unknown', label);
}
